import DOMPurify from "dompurify";

export const FAQ_LAST_UPDATED = "September 26, 2026";

const ALLOWED_TAGS = [
  "p",
  "br",
  "ul",
  "ol",
  "li",
  "a",
  "strong",
  "em",
  "code",
  "pre",
  "blockquote",
  "h3",
  "h4",
];

const ALLOWED_ATTR = ["href", "target", "rel", "title"];

export const sanitizeFaqAnswerHtml = (html) => {
  const value = typeof html === "string" ? html : "";
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
};

export const stripHtmlToText = (html) => {
  if (typeof html !== "string" || html.trim().length === 0) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return (doc.body?.textContent ?? "").replace(/\s+/g, " ").trim();
};

export const getPublishedFaqRecords = (records) => {
  const list = Array.isArray(records) ? records : [];
  return list
    .filter((record) => Boolean(record?.published))
    .slice()
    .sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));
};

export const getLandingFaqPreview = (records, limit = 6) => {
  const published = getPublishedFaqRecords(records);
  const preferred = published.filter((record) => Boolean(record?.showOnLanding));
  const chosen = (preferred.length > 0 ? preferred : published).slice(0, limit);
  return chosen;
};

export const isFaqEligibleForSchema = (record) => {
  if (!record?.published) return false;
  if (record?.excludeFromSchema) return false;

  const question = String(record?.question ?? "").trim();
  if (question.length === 0) return false;

  const safeHtml = sanitizeFaqAnswerHtml(record?.answerHtml ?? "");
  const text = stripHtmlToText(safeHtml);
  return text.length > 0;
};

export const filterFaqRecords = (records, query) => {
  const q = String(query ?? "").trim().toLowerCase();
  if (!q) return records;

  return records.filter((record) => {
    const question = String(record?.question ?? "").toLowerCase();
    const safeHtml = sanitizeFaqAnswerHtml(record?.answerHtml ?? "");
    const answerText = stripHtmlToText(safeHtml).toLowerCase();
    return question.includes(q) || answerText.includes(q);
  });
};

export const buildFaqPageJsonLd = (records) => {
  const eligible = (Array.isArray(records) ? records : []).filter(isFaqEligibleForSchema);

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: eligible.map((record) => {
      const safeHtml = sanitizeFaqAnswerHtml(record?.answerHtml ?? "");
      const text = stripHtmlToText(safeHtml);

      return {
        "@type": "Question",
        name: String(record?.question ?? "").trim(),
        acceptedAnswer: {
          "@type": "Answer",
          text,
        },
      };
    }),
  };
};

